import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../../Components/Views/ViewItem";
import { MailHtmlFrame, formatFileSize, getInitial, linkifyText, renderSignaturePreview, sanitizeEmailHtml } from "../../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../../Controllers/Controllers";
import { STATUS_LABELS, LeadCategoryType, LeadType, LeadsPagination } from "../../../../DataTypes/leads";
import { ComposeEmailFields, EmailMessageType } from "../../../../DataTypes/mail";
import { getRoundedLocalDateTimeString } from "../../../../Helpers/Helpers";
import { RECIPIENT_MODE_OPTIONS, RecipientMode, getDefaultRecipientMode, getRecipientOptionLabel, getResolvedRecipients } from "../../../../Helpers/LeadRecipients";
import { SourceFilter, SourceSelectView } from "../../../UIComponents/SourceSelectView";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import '../Mail/mail-styles.css'
import '../../Settings/settings-styles.css'
import './communications-styles.css'
import { navigator } from "../../../../Controllers/Navigator";

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

type CommunicationsPageProps = viewItemProps & {
    leadId?: string;
}

const SOURCE_FILTER_VALUES: SourceFilter[] = ['all', 'outreach', 'research']

const parseFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    const page = Number(params.get('page'))
    const status = params.get('status') || 'all'
    const source = params.get('source') || 'all'
    return {
        page: Number.isInteger(page) && page > 0 ? page : 1,
        status: status in STATUS_LABELS ? status : 'all',
        search: params.get('search') || '',
        source: (SOURCE_FILTER_VALUES as string[]).includes(source) ? source as SourceFilter : 'all',
        categoryId: params.get('category') || 'all',
    }
}

const emptyReplyFields: ComposeEmailFields = {
    to: '',
    cc: '',
    subject: '',
    body_text: '',
    include_signature: true,
}

type AccountSignatureFields = {
    display_name: string;
    position: string;
    phone: string;
    email_address: string;
}

type CommunicationsPageState = {
    leads: LeadType[];
    pagination: LeadsPagination | null;
    leadsLoading: boolean;
    leadsError: string | null;
    needsAuth: boolean;
    search: string;
    status: string;
    source: SourceFilter;
    categories: LeadCategoryType[];
    categoryId: string;
    page: number;

    selectedLead: LeadType | null;
    timelineEmails: EmailMessageType[];
    timelineLoading: boolean;
    timelineError: string | null;

    replyOpen: boolean;
    replyFields: ComposeEmailFields;
    recipientMode: RecipientMode;
    replySending: boolean;
    replyError: string | null;

    signatureTemplateHtml: string;
    accountFields: AccountSignatureFields | null;
    signatureLoading: boolean;
}

class CommunicationsPage extends ViewItem {
    state: CommunicationsPageState;
    props: CommunicationsPageProps;
    searchTimer: any = null;

    constructor(props: CommunicationsPageProps) {
        super(props)
        this.props = props
        const urlFilters = parseFiltersFromUrl()
        this.state = {
            leads: [],
            pagination: null,
            leadsLoading: true,
            leadsError: null,
            needsAuth: false,
            categories: [],
            ...urlFilters,

            selectedLead: null,
            timelineEmails: [],
            timelineLoading: false,
            timelineError: null,

            replyOpen: false,
            replyFields: { ...emptyReplyFields },
            recipientMode: 'decision_maker',
            replySending: false,
            replyError: null,

            signatureTemplateHtml: '',
            accountFields: null,
            signatureLoading: true,
        }
    }

    componentDidMount(): void {
        this.syncUrlWithState()
        this.loadLeads()
        this.loadCategories()
        this.loadSignatureContext()
        if (this.props.leadId) this.loadLeadFromUrl()
    }

    componentWillUnmount(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer)
    }

    syncUrlWithState = () => {
        const { page, status, search, source, categoryId } = this.state
        const params = new URLSearchParams()
        if (page > 1) params.set('page', String(page))
        if (status !== 'all') params.set('status', status)
        if (search) params.set('search', search)
        if (source !== 'all') params.set('source', source)
        if (categoryId !== 'all') params.set('category', categoryId)
        const qs = params.toString()
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '')
        window.history.replaceState(null, '', newUrl)
    }

    applyFilters = () => {
        this.syncUrlWithState()
        this.loadLeads()
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    componentDidUpdate(prevProps: CommunicationsPageProps): void {
        if (prevProps.leadId !== this.props.leadId && this.props.leadId) {
            this.loadLeadFromUrl()
        }
    }

    loadLeadFromUrl = async () => {
        const requestedLeadId = this.props.leadId
        const leadId = Number(requestedLeadId)
        if (!leadId || !controllers().auth.userToken) return
        const response = await controllers().outreach.getLeadDetail(leadId)
        if (this.props.leadId !== requestedLeadId) return // props changed again while this was in flight
        if (response && response.statusCode === 200 && response.data) {
            this.selectLead(response.data)
        }
    }

    loadLeads = async () => {
        const { search, page, status, source, categoryId } = this.state
        this.setState({ leadsLoading: true, leadsError: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ leadsLoading: false, needsAuth: true })
            return
        }
        const response = await controllers().outreach.getLeads({
            search: search || undefined,
            page,
            per_page: PER_PAGE,
            sort: 'last_message',
            status: status !== 'all' ? status : undefined,
            is_from_research: source === 'all' ? undefined : source === 'research',
            category_id: categoryId !== 'all' ? Number(categoryId) : undefined,
        })
        if (!response || response.statusCode !== 200) {
            this.setState({ leadsLoading: false, leadsError: response?.error || 'Failed to load leads.' })
            return
        }
        this.setState({
            leads: response.data?.leads || [],
            pagination: response.data?.pagination || null,
            leadsLoading: false,
        })
    }

    handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.applyFilters)
        }, SEARCH_DEBOUNCE_MS)
    }

    handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ status: e.target.value, page: 1 }, this.applyFilters)
    }

    handleSourceChange = (value: SourceFilter) => {
        this.setState({ source: value, page: 1 }, this.applyFilters)
    }

    handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ categoryId: e.target.value, page: 1 }, this.applyFilters)
    }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.applyFilters)
    }

    selectLead = (lead: LeadType) => {
        this.setState({ selectedLead: lead })
        this.loadTimeline(lead)
    }

    loadTimeline = async (lead: LeadType) => {
        this.setState({ timelineLoading: true, timelineError: null })
        const response = await controllers().mail.getLeadEmailTimeline(lead.id)
        if (this.state.selectedLead?.id !== lead.id) return // user already moved on

        if (!response || response.statusCode !== 200) {
            this.setState({ timelineLoading: false, timelineError: response?.error || 'Failed to load communication history.', timelineEmails: [] })
            return
        }
        this.setState({ timelineEmails: response.data?.emails || [], timelineLoading: false })
    }

    loadSignatureContext = async () => {
        this.setState({ signatureLoading: true })
        if (!controllers().auth.userToken) {
            this.setState({ signatureLoading: false })
            return
        }
        const [signatureResponse, settingsResponse] = await Promise.all([
            controllers().mail.getSharedSignature(),
            controllers().mail.getSettings(),
        ])
        const account = settingsResponse?.data
        this.setState({
            signatureLoading: false,
            signatureTemplateHtml: (signatureResponse && signatureResponse.statusCode === 200) ? (signatureResponse.data?.html || '') : '',
            accountFields: (settingsResponse && settingsResponse.statusCode === 200 && account) ? {
                display_name: account.display_name || '',
                position: account.position || '',
                phone: account.phone || '',
                email_address: account.email_address || '',
            } : null,
        })
    }

    // The lead itself carries the addresses to reply to (decision maker /
    // company — same choice as Compose), not whichever address happens to
    // appear on the latest message.
    openReply = () => {
    const { selectedLead, timelineEmails } = this.state
    if (!selectedLead) return
    const latest = timelineEmails[0]
    const subject = latest
        ? (latest.subject?.toLowerCase().startsWith('re:') ? latest.subject : `Re: ${latest.subject || ''}`)
        : `Re: ${selectedLead.company_name}`
    this.setState({
        replyOpen: true,
        replyError: null,
        recipientMode: getDefaultRecipientMode(selectedLead),
        replyFields: {
            to: '',
            cc: '',
            subject,
            body_text: '',
            include_signature: true,
            lead_id: selectedLead.id,
            in_reply_to_message_id: latest?.message_id,
        },
    })
}

    closeReply = () => {
        if (this.state.replySending) return
        this.setState({ replyOpen: false })
    }

    setReplyField = (field: keyof ComposeEmailFields, value: string | boolean) => {
        this.setState((prev: CommunicationsPageState) => ({
            replyFields: { ...prev.replyFields, [field]: value }
        }))
    }

    handleRecipientModeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ recipientMode: e.target.value as RecipientMode, replyError: null })
    }

    handleSendReply = async () => {
        const { replyFields, selectedLead, recipientMode } = this.state
        if (!selectedLead) return

        const recipients = getResolvedRecipients(selectedLead, recipientMode)
        if (!recipients.length) {
            this.setState({ replyError: 'This lead has no email address for the selected option.' })
            return
        }

        this.setState({ replySending: true, replyError: null })
        const results = await Promise.all(recipients.map(to => controllers().mail.sendEmail({ ...replyFields, to })))
        const failures = results.filter(r => !r || r.statusCode !== 200)

        if (failures.length === results.length) {
            this.setState({ replySending: false, replyError: failures[0]?.error || 'Failed to send reply.' })
            return
        }

        this.setState({ replySending: false, replyOpen: false })
        this.loadTimeline(selectedLead)
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination communications-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <div className="outreach-pagination-controls">
                    <button className="outreach-pagination-btn" disabled={!pagination.has_previous} onClick={() => this.goToPage(page - 1)}>
                        Previous
                    </button>
                    <button className="outreach-pagination-btn" disabled={!pagination.has_next} onClick={() => this.goToPage(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderLeadsList = () => {
        const { leads, selectedLead } = this.state
        return (
            <div className="mail-list">
                {leads.map(lead => (
                    <div
                        key={lead.id}
                        className={`mail-list-item ${selectedLead?.id === lead.id ? 'mail-list-item-active' : ''}`}
                        onClick={() => this.selectLead(lead)}
                    >
                        <div className="mail-list-item-top">
                            <span className="mail-list-item-sender">{lead.company_name}</span>
                            <span className={`communications-status-chip lead-status-${lead.status}`}>
                                {STATUS_LABELS[lead.status] || lead.status}
                            </span>
                        </div>
                        <div className="mail-list-item-subject">
                            {lead.decision_maker_name || 'No contact name'}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    renderReplyBox = () => {
        const { replyOpen, replyFields, recipientMode, replySending, replyError, selectedLead, signatureTemplateHtml, accountFields, signatureLoading } = this.state
        if (!replyOpen || !selectedLead) return null
        return (
            <div className="mail-reply-box">
                <div className="mail-reply-box-header">
                    <span className="mail-reply-box-title">Reply</span>
                    <button className="mail-thread-icon-btn" onClick={this.closeReply} disabled={replySending} aria-label="Cancel reply">
                        ×
                    </button>
                </div>

                {replyError && <div className="category-form-error">{replyError}</div>}

                <div className="category-form-group">
                    <label className="category-form-label">To</label>
                    <select
                        className="category-form-input"
                        value={recipientMode}
                        onChange={this.handleRecipientModeChange}
                        disabled={replySending}
                    >
                        {RECIPIENT_MODE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value} disabled={getResolvedRecipients(selectedLead, value).length === 0}>
                                {getRecipientOptionLabel(selectedLead, value, label)}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="category-form-group">
                    <label className="category-form-label">Subject</label>
                    <input
                        className="category-form-input"
                        value={replyFields.subject}
                        onChange={(e) => this.setReplyField('subject', e.target.value)}
                        disabled={replySending}
                    />
                </div>
                <div className="category-form-group">
                    <textarea
                        className="category-form-textarea mail-reply-textarea"
                        value={replyFields.body_text}
                        onChange={(e) => this.setReplyField('body_text', e.target.value)}
                        placeholder="Write your reply..."
                        rows={6}
                        disabled={replySending}
                        autoFocus
                    />
                </div>

                <label className="category-form-checkbox-row mail-reply-signature-toggle">
                    <input
                        type="checkbox"
                        checked={replyFields.include_signature}
                        onChange={(e) => this.setReplyField('include_signature', e.target.checked)}
                        disabled={replySending}
                    />
                    Include signature
                </label>

                {replyFields.include_signature && (
                    <div className="settings-signature-preview">
                        <span className="settings-signature-preview-label">Signature Preview</span>
                        {signatureLoading && (
                            <div className="outreach-state">
                                <div className="outreach-spinner" />
                            </div>
                        )}
                        {!signatureLoading && signatureTemplateHtml && (
                            <MailHtmlFrame html={sanitizeEmailHtml(renderSignaturePreview(signatureTemplateHtml, accountFields || {}))} />
                        )}
                        {!signatureLoading && !signatureTemplateHtml && (
                            <p className="outreach-muted">No signature template has been set up yet.</p>
                        )}
                    </div>
                )}

                <div className="mail-reply-box-actions communications-reply-actions">
                    <div className="mail-reply-box-buttons">
                        <button className="outreach-modal-cancel" onClick={this.closeReply} disabled={replySending}>
                            Cancel
                        </button>
                        <button className="outreach-add-btn" onClick={this.handleSendReply} disabled={replySending}>
                            {replySending ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderTimeline = () => {
        const { selectedLead, timelineEmails, timelineLoading, timelineError } = this.state
        const userToken = controllers().auth.userToken

        if (!selectedLead) {
            return (
                <div className="mail-thread-panel mail-thread-empty">
                    <span className="outreach-muted">Select a lead to view its communication history.</span>
                </div>
            )
        }

        return (
            <div className="mail-thread-panel">
                <div className="mail-thread-header">
                    <div className="mail-thread-header-info">
                        <h2 className="mail-thread-subject">{selectedLead.company_name}</h2>
                        <p className="mail-thread-participants">
                            {timelineEmails.length} message{timelineEmails.length === 1 ? '' : 's'}
                        </p>
                    </div>
                    <div className="mail-thread-header-actions">
                        <button className="outreach-edit-btn" onClick={this.openReply}>
                            Reply
                        </button>
                    </div>
                </div>

                {this.renderReplyBox()}

                {timelineLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!timelineLoading && timelineError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{timelineError}</span>
                        <button className="outreach-banner-action" onClick={() => this.loadTimeline(selectedLead)}>
                            Retry
                        </button>
                    </div>
                )}

                {!timelineLoading && !timelineError && timelineEmails.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No emails on record with this lead yet.
                        <button
                            onClick={()=>navigator().navigate(`/outreach/compose/${selectedLead.id}`)}
                            className="outreach-add-btn communications-send-new-btn"
                        >
                            Send new email
                        </button>
                    </div>
                )}

                {!timelineLoading && !timelineError && timelineEmails.length > 0 && (
                    <div className="mail-thread-messages">
                        {timelineEmails.map(msg => {
                            const isOutgoing = msg.direction === 'outgoing'
                            return (
                                <div key={msg.id} className="mail-thread-message">
                                    <div className={`mail-thread-message-avatar ${isOutgoing ? 'mail-thread-message-avatar-you' : ''}`}>
                                        {getInitial(msg.from_address)}
                                    </div>
                                    <div className="mail-thread-message-content">
                                        <div className="mail-thread-message-header">
                                            <div className="mail-thread-message-from-block">
                                                <span className="mail-thread-message-from">{msg.from_address}</span>
                                                <span className="mail-thread-message-to">to {msg.to_addresses}</span>
                                                {msg.account_email && (
                                                    <span className="communications-account-tag">via {msg.account_email}</span>
                                                )}
                                            </div>
                                            <span className="mail-thread-message-date">
                                                {getRoundedLocalDateTimeString(msg.sent_at || msg.created_at)}
                                            </span>
                                        </div>
                                        {msg.body_html ? (
                                            <MailHtmlFrame html={sanitizeEmailHtml(msg.body_html, msg.attachments || [], userToken)} />
                                        ) : (
                                            <div className="mail-thread-message-body">
                                                {msg.body_text ? linkifyText(msg.body_text) : <span className="outreach-muted">(no content)</span>}
                                            </div>
                                        )}

                                        {msg.attachments?.some(a => !a.is_inline) && (
                                            <div className="mail-attachments">
                                                {msg.attachments.filter(a => !a.is_inline).map(att => (
                                                    <a
                                                        key={att.id}
                                                        className="mail-attachment-chip"
                                                        href={`${att.url}${userToken ? `?token=${userToken}` : ''}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <span className="mail-attachment-name">{att.filename}</span>
                                                        <span className="mail-attachment-size">{formatFileSize(att.size)}</span>
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { leads, leadsLoading, leadsError, needsAuth, search, status, source, categories, categoryId } = this.state

        return (
            <div className="outreach-page">

                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Communications</h1>
                        <p className="outreach-subtitle">Find new companies and add them straight into your pipeline.</p>
                    </div>
                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search leads..."
                            value={search}
                            onChange={this.handleSearchChange}
                        />
                        <select className="outreach-status-select" value={status} onChange={this.handleStatusChange}>
                            <option value="all">All statuses</option>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <select className="outreach-status-select" value={categoryId} onChange={this.handleCategoryChange}>
                            <option value="all">All categories</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <SourceSelectView
                            source={source}
                            onSourceChange={this.handleSourceChange}
                        />
                    </div>
                </div>

                {!leadsLoading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to view communications.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!leadsLoading && leadsError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{leadsError}</span>
                        <button className="outreach-banner-action" onClick={this.loadLeads}>
                            Retry
                        </button>
                    </div>
                )}

                {leadsLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No leads found.
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length > 0 && (
                    <div className="mail-layout">
                        <div className="mail-list-column">
                            {this.renderLeadsList()}
                            {this.renderPagination()}
                        </div>
                        {this.renderTimeline()}
                    </div>
                )}
            </div>
        )
    }
}

export { CommunicationsPage }
